import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import { joinCommunity, leaveCommunity } from '../../api/communities';

/**
 * CommunityCard component for displaying a community
 * @param {Object} props - Component props
 * @param {Object} props.community - Community data
 * @param {boolean} props.compact - Whether to show a compact version
 * @param {boolean} props.showActions - Whether to show action buttons
 * @param {Function} props.onMembershipChange - Callback for membership change
 * @returns {JSX.Element} CommunityCard component
 */
const CommunityCard = ({
  community,
  compact = false,
  showActions = true,
  onMembershipChange,
}) => {
  const { user, isAuth } = useAuth();
  const [isMember, setIsMember] = useState(
    user && community.members ? community.members.includes(user._id) : false
  );
  const [loading, setLoading] = useState(false);
  
  // Handle join/leave community
  const handleToggleMembership = async () => {
    if (!isAuth) return;
    
    try {
      setLoading(true);
      
      if (isMember) {
        await leaveCommunity(community._id);
      } else {
        await joinCommunity(community._id);
      }
      
      setIsMember(!isMember);
      
      if (onMembershipChange) {
        onMembershipChange(community._id, !isMember);
      }
    } catch (error) {
      console.error('Error toggling membership:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Render compact version
  if (compact) {
    return (
      <div className="flex items-center">
        <Link to={`/communities/${community.slug}`} className="flex-shrink-0">
          <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden">
            {community.profilePicture ? (
              <img
                src={community.profilePicture}
                alt={community.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-medium">
                {community.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </Link>
        
        <div className="ml-3 min-w-0 flex-1">
          <Link
            to={`/communities/${community.slug}`}
            className="text-sm font-medium text-gray-900 hover:underline truncate"
          >
            {community.name}
          </Link>
          <p className="text-xs text-gray-500 truncate">
            {community.memberCount || 0} members
          </p>
        </div>
        
        {showActions && isAuth && (
          <div className="ml-2 flex-shrink-0">
            <Button
              variant={isMember ? 'outline' : 'primary'}
              size="xs"
              onClick={handleToggleMembership}
              isLoading={loading}
              isDisabled={loading}
            >
              {isMember ? 'Joined' : 'Join'}
            </Button>
          </div>
        )}
      </div>
    );
  }
  
  // Render full version
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Community cover */}
      <div className="h-32 bg-gray-200 relative">
        {community.coverPicture ? (
          <img
            src={community.coverPicture}
            alt={`${community.name} cover`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-blue-400 to-indigo-500"></div>
        )}
        
        {/* Community profile image */}
        <div className="absolute bottom-0 left-4 transform translate-y-1/2 w-16 h-16 bg-white rounded-full p-1">
          <div className="w-full h-full bg-gray-200 rounded-full overflow-hidden">
            {community.profilePicture ? (
              <img
                src={community.profilePicture}
                alt={community.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white text-xl font-medium">
                {community.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
        
        {/* Private badge */}
        {community.isPrivate && (
          <div className="absolute top-2 right-2 bg-gray-900 bg-opacity-50 text-white px-2 py-1 rounded-full text-xs font-medium">
            Private
          </div>
        )}
      </div>
      
      {/* Community info */}
      <div className="pt-10 px-4 pb-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <Link
              to={`/communities/${community.slug}`}
              className="text-lg font-bold text-gray-900 hover:underline"
            >
              {community.name}
            </Link>
            <p className="text-sm text-gray-500">
              {community.memberCount || 0} members
            </p>
          </div>
          
          {showActions && isAuth && (
            <Button
              variant={isMember ? 'outline' : 'primary'}
              size="sm"
              onClick={handleToggleMembership}
              isLoading={loading}
              isDisabled={loading}
            >
              {isMember ? 'Joined' : 'Join'}
            </Button>
          )}
        </div>
        
        {community.description && (
          <p className="text-sm text-gray-700 line-clamp-2 mb-3">
            {community.description}
          </p>
        )}
        
        {/* Category */}
        {community.category && (
          <div className="flex items-center text-sm text-gray-500">
            <svg
              className="h-4 w-4 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
            <span>{community.category}</span>
          </div>
        )}
      </div>
    </div>
  );
};

CommunityCard.propTypes = {
  community: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  showActions: PropTypes.bool,
  onMembershipChange: PropTypes.func,
};

export default CommunityCard;